import apiServiceManager from './apiServiceManager';
import type { Course } from '../types/canvas';
import type { StoredCanvasCredentials } from '../types/auth';

// Import verification for development
if (import.meta.env.DEV) {
  console.log('apiServiceManager loaded:', !!apiServiceManager);
}

/**
 * Reads one property off a caught value.
 *
 * `catch` binds `unknown` under `strict`. The two catch blocks below want
 * axios's fields (`response.status`, `response.data`) alongside `message`,
 * so narrowing with `instanceof Error` would not reach them, and asserting
 * the rejection into an axios error shape would be claiming something
 * neither this file nor the compiler has checked -- a rejected promise can
 * carry any value.
 *
 * The check here is real: after `typeof value === 'object' && value !==
 * null`, viewing the value as `Record<string, unknown>` is true, because
 * every property of a non-null object is readable and `unknown` asserts
 * nothing about what came back. Callers below keep the result as `unknown`
 * rather than narrowing it further, which is what makes the logged output
 * and the rethrown message identical to what the unguarded reads produced.
 */
const readProp = (value: unknown, key: string): unknown =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)[key]
    : undefined;

/**
 * Store Canvas API credentials in DynamoDB with KMS encryption
 * @param token - Canvas API token
 * @param userId - Canvas user ID
 * @param rootUrl - Canvas instance URL
 * @returns Response with success/failure message
 */
export const storeCanvasCredentials = async (
  token: string, 
  userId: string, 
  rootUrl: string
): Promise<any> => {
  try {
    const response = await apiServiceManager.storeCredentials({
      'token': token,
      'canvasUserId': userId,
      'canvasRootUrl': rootUrl
    });
    
    return response;
  } catch (error) {
    console.error('Failed to store Canvas credentials:', error);
    throw new Error('Failed to securely store Canvas credentials');
  }
};

/**
 * Fetch stored Canvas API credentials for the current user
 *
 * The server (GetCredentials) always responds with a JSON array -- it
 * queries DynamoDB with `response.get('Items', [])` and `json.dumps`s that
 * list directly, never unwrapping a single-item result to a bare object.
 * An empty array means "no credentials stored yet", not an absent response.
 * See Lambda_Individual/GetCredentials/canvas_get_credentials.py.
 *
 * @returns Stored credentials, one entry per stored credential row
 */
export const fetchCanvasCredentials = async (): Promise<StoredCanvasCredentials[]> => {
    try {
      console.log('=== Fetching Canvas Credentials ===');
      console.log('Environment - USE_AUTH:', import.meta.env.VITE_USE_AUTH);
      console.log('Environment - API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
      console.log('Current base URL from config:', apiServiceManager);
      
      const response = await apiServiceManager.getCredentials();
      console.log('Canvas credentials response:', response);
      return response;
    } catch (error) {
      const message = readProp(error, 'message');
      console.error('=== Canvas Credentials Error ===');
      console.error('Failed to fetch Canvas credentials:', error);
      console.error('Error details:', {
        message,
        status: readProp(error, 'status'),
        response: readProp(error, 'response')
      });
      // `message || 'Unknown error'` is kept verbatim rather than switched to
      // `??`: the original falls back for an empty-string message too, and
      // this message reaches the user.
      throw new Error(`Failed to retrieve your Canvas credentials: ${message || 'Unknown error'}`);
    }
  };

/**
 * Test Canvas API credentials by making a simple request
 * @param token - Canvas API token
 * @param userId - Canvas user ID
 * @param rootUrl - Canvas instance URL
 * @returns Whether the credentials are valid
 */
export const testCanvasCredentials = async (token: string, userId: string, rootUrl: string): Promise<any> => {
  try {
    console.log('🧪 testCanvasCredentials called with:', {
      token: token ? '[REDACTED]' : 'MISSING',
      userId,
      rootUrl,
      tokenLength: token ? token.length : 0
    });

    // Call the AWS API Gateway endpoint for token validation
    console.log('🧪 About to call apiServiceManager.validateToken...');
    const response = await apiServiceManager.validateToken(token, userId, rootUrl);
    console.log('🧪 apiServiceManager.validateToken response:', response);
    console.log('🧪 Response type:', typeof response);
    console.log('🧪 Response keys:', response ? Object.keys(response) : 'null/undefined');

    return response;
  } catch (error) {
    const response = readProp(error, 'response');
    const message = readProp(error, 'message');
    console.error('🧪 Canvas credentials test failed:', error);
    console.error('🧪 Error details:', {
      name: readProp(error, 'name'),
      message,
      status: readProp(response, 'status'),
      statusText: readProp(response, 'statusText'),
      data: readProp(response, 'data')
    });
    // `message` stays `unknown` rather than being coerced to a string: this
    // function's declared return is `Promise<any>`, so tightening it here
    // would buy no caller any safety while risking a different value than
    // the unguarded `error.message` produced.
    return { valid: false, error: message };
  }
};

/**
 * Fetch courses for the current user
 * @param token - Canvas API token
 * @param userId - Canvas user ID
 * @param rootUrl - Canvas instance URL
 * @param courseId - Optional specific course ID to fetch
 * @returns List of courses or single course
 */
export const fetchCourses = async (token: string, userId: string, rootUrl: string, courseId: string | number | null = null): Promise<Course[]> => {
  try {
    const credentials = { token, userId, rootUrl };
    
    console.log('Fetching courses with credentials:', { userId, rootUrl });
    
    if (courseId) {
      // Fetch specific course details
      return await apiServiceManager.getCourseDetails(courseId, credentials);
    } else {
      // Caching now lives at the TanStack Query layer (CanvasDataContext),
      // not here -- see task 4.4a/4.4b.
      return await apiServiceManager.getCourses(credentials);
    }
  } catch (error) {
    console.error('Failed to fetch courses:', error);
    throw new Error('Failed to load courses from Canvas.');
  }
};


/**
 * Fetch detailed course data including assignments and grades
 * @param token - Canvas API token
 * @param userId - Canvas user ID
 * @param rootUrl - Canvas instance URL
 * @param courseId - Course ID
 * @returns Course details with assignments and grades
 */
export const fetchCourseDetails = async (token: string, userId: string, rootUrl: string, courseId: string | number): Promise<any> => {
  try {
    const credentials = { token, userId, rootUrl };
    
    // Use enhanced API with caching
    const response = await apiServiceManager.getCourseDetails(courseId, credentials);
    
    return response;
  } catch (error) {
    console.error(`Failed to fetch details for course ${courseId}:`, error);
    throw new Error(`Failed to load course details from Canvas.`);
  }
};


/**
 * Fetch historical grade data for a course
 * @param token - Canvas API token
 * @param userId - Canvas user ID
 * @param rootUrl - Canvas instance URL
 * @param courseId - Course ID
 * @returns Historical grade data points
 */
export const fetchCourseHistory = async (token: string, userId: string, rootUrl: string, courseId: string | number): Promise<any[]> => {
  try {
    const credentials = { token, userId, rootUrl };
    
    // Use enhanced API with caching
    const response = await apiServiceManager.getCourseHistory(courseId, credentials);
    return response;
  } catch (error) {
    console.error(`Failed to fetch history for course ${courseId}:`, error);
    throw new Error(`Failed to load course history data.`);
  }
};

/**
 * Fetch stored snapshots for the current user
 * @param userId - User ID
 * @returns List of snapshots
 */
export const fetchSnapshots = async (userId: string): Promise<any[]> => {
  try {
    const response = await apiServiceManager.getSnapshots(userId);
    return response;
  } catch (error) {
    console.error('Failed to fetch snapshots:', error);
    throw new Error('Failed to load snapshots.');
  }
};

/**
 * Store a new snapshot
 * @param snapshotData - Snapshot data to store
 * @returns Response with success/failure message
 */
export const storeSnapshot = async (snapshotData: any): Promise<any> => {
  try {
    const response = await apiServiceManager.storeSnapshot(snapshotData);
    return response;
  } catch (error) {
    console.error('Failed to store snapshot:', error);
    throw new Error('Failed to save snapshot.');
  }
};

/**
 * Delete a snapshot
 * @param snapshotId - Snapshot ID to delete
 * @param userId - User ID
 * @returns Response with success/failure message
 */
export const deleteSnapshot = async (snapshotId: string, userId: string): Promise<any> => {
  try {
    const response = await apiServiceManager.deleteSnapshot(snapshotId, userId);
    return response;
  } catch (error) {
    console.error('Failed to delete snapshot:', error);
    throw new Error('Failed to delete snapshot.');
  }
};

/**
 * Fetch assignment notes for a user
 * @param userId - User ID
 * @returns Array of assignment notes
 */
export const fetchAssignmentNotes = async (userId: string): Promise<any[]> => {
  try {
    return await apiServiceManager.getAssignmentNotes(userId);
  } catch (error) {
    console.error('Failed to fetch assignment notes:', error);
    throw new Error('Failed to load assignment notes.');
  }
};

/**
 * Save (create or update) an assignment note. POST handles both cases since
 * there's only one note per assignment.
 * @param noteData - userId, courseId, assignmentId, note, updatedAt
 * @returns The saved note as persisted
 */
export const saveAssignmentNote = async (noteData: any): Promise<any> => {
  try {
    return await apiServiceManager.saveAssignmentNote(noteData);
  } catch (error) {
    console.error('Failed to save assignment note:', error);
    throw new Error('Failed to save assignment note.');
  }
};

/**
 * Delete an assignment note
 * @param courseId - Course ID
 * @param assignmentId - Assignment ID
 */
export const deleteAssignmentNote = async (courseId: string, assignmentId: string): Promise<any> => {
  try {
    return await apiServiceManager.deleteAssignmentNote(courseId, assignmentId);
  } catch (error) {
    console.error('Failed to delete assignment note:', error);
    throw new Error('Failed to delete assignment note.');
  }
};

/**
 * Update the default Canvas user preference for the authenticated user
 * @param newDefaultUserId - The Canvas user ID to set as default
 * @param canvasUsers - The complete array of Canvas users with updated default flags
 * @returns Response with success/failure message
 */
export const updateDefaultUser = async (
  newDefaultUserId: string,
  canvasUsers: any[]
): Promise<any> => {
  try {
    const response = await apiServiceManager.updateDefaultUser({
      newDefaultUserId,
      canvasUsers
    });

    return response;
  } catch (error) {
    console.error('Failed to update default user preference:', error);
    throw new Error('Failed to update default user preference');
  }
};


/**
 * Fetch the current user's notification/digest preferences (task 3.5).
 * Always returns a value - the backend defaults a missing PREFERENCES row
 * rather than 404ing, so this never needs a "no preferences yet" branch.
 * @returns Notification preferences: digestFrequency, gradeDropThreshold,
 *   missingAssignmentThreshold, disabledChildren
 */
export const fetchNotificationPreferences = async (): Promise<any> => {
  try {
    return await apiServiceManager.getNotificationPreferences();
  } catch (error) {
    console.error('Failed to fetch notification preferences:', error);
    throw new Error('Failed to load notification settings');
  }
};

/**
 * Save the current user's notification/digest preferences (task 3.5)
 * @param preferences - digestFrequency, gradeDropThreshold,
 *   missingAssignmentThreshold, disabledChildren
 * @returns The saved preferences as persisted (never includes
 *   unsubscribeToken - the backend never returns it)
 */
export const saveNotificationPreferences = async (preferences: any): Promise<any> => {
  try {
    return await apiServiceManager.saveNotificationPreferences(preferences);
  } catch (error) {
    console.error('Failed to save notification preferences:', error);
    throw new Error('Failed to save notification settings');
  }
};

const canvasService = {
  storeCanvasCredentials,
  fetchCanvasCredentials,
  testCanvasCredentials,
  fetchCourses,
  fetchCourseDetails,
  fetchCourseHistory,
  fetchSnapshots,
  storeSnapshot,
  deleteSnapshot,
  fetchAssignmentNotes,
  saveAssignmentNote,
  deleteAssignmentNote,
  updateDefaultUser,
  fetchNotificationPreferences,
  saveNotificationPreferences
};

export default canvasService;